import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  type?: string;
  noindex?: boolean;
  jsonLd?: object[];
  canonical?: string;
}

/**
 * Lightweight SEO head manager — sets document.title and meta tags via DOM API.
 * Avoids react-helmet-async dual-React issues.
 */
const SEOHead = ({ title, description, type = "website", noindex = false, jsonLd = [], canonical }: SEOHeadProps) => {
  useEffect(() => {
    document.title = title;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);

    // Canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    if (noindex) {
      setMeta("name", "robots", "noindex");
    } else {
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta && robotsMeta.getAttribute("content") === "noindex") {
        robotsMeta.remove();
      }
    }

    // JSON-LD
    const existingScripts = document.querySelectorAll('script[data-seo-head]');
    existingScripts.forEach((s) => s.remove());

    jsonLd.forEach((data) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-head", "true");
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });

    return () => {
      const scripts = document.querySelectorAll('script[data-seo-head]');
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, type, noindex, jsonLd, canonical]);

  return null;
};

export default SEOHead;
